import { routes } from "@constants/routes";
import { ViewportLoading } from "@cpj-ai/panel-npm-package";

import { lazy, ReactNode, Suspense } from "react";
import { createBrowserRouter, Navigate, RouteObject } from "react-router-dom";

const ProtectedRoute = lazy(() => import("./ProtectedRoute"));

// Layouts
const MainLayout = lazy(() => import("@layouts/Main"));
const AuthLayout = lazy(() => import("@layouts/Auth"));

// Auth Pages
const Register = lazy(() => import("@pages/Auth/Register"));
const VerifyAccountPage = lazy(() => import("@pages/Auth/VerifyAccount"));

const SetPasswordCompletedPage = lazy(() => import("@pages/Auth/SetPasswordCompleted"));
const SetPasswordLinkSentPage = lazy(() => import("@pages/Auth/SetPasswordLinkSent"));
const SetPasswordPage = lazy(() => import("@pages/Auth/SetPassword"));

const Login = lazy(() => import("@pages/Auth/Login"));

const ForgetPassword = lazy(() => import("@pages/Auth/ForgetPassword"));
const ForgetPasswordCompleted = lazy(() => import("@pages/Auth/ForgetPasswordCompleted"));
const ForgetPasswordLinkSent = lazy(() => import("@pages/Auth/ForgetPasswordLinkSent"));
const ResetPassword = lazy(() => import("@pages/Auth/ResetPassword"));

const NotFoundPage = lazy(() => import("@pages/NotFound"));
const ErrorPage = lazy(() => import("@pages/Error"));

// Main Pages
const JobPositionListPage = lazy(() => import("@pages/JobPosition"));
const AddJobPositionPage = lazy(() => import("@pages/JobPosition/Add"));
const ViewJobPositionPage = lazy(() => import("@pages/JobPosition/View"));
const CandidatePage = lazy(() => import("@pages/JobPosition/Candidate"));
const PreviewJobPositionPage = lazy(() => import("@modules/JobPosition/pages/AddJobPosition/Preview"));

const ProfilePage = lazy(() => import("@pages/Profile"));

const SettingPage = lazy(() => import("@pages/Setting"));
const PostHiringPage = lazy(() => import("@pages/PostHiring"));
const PostHiringEmployeeDetailsPage = lazy(() => import("@pages/PostHiring/EmployeeDetails"));
const FinancialPage = lazy(() => import("@pages/Financial"));

const SupportPage = lazy(() => import("@pages/Support"));

const InterviewsPage = lazy(() => import("@pages/Interviews"));

type RouteConfig = {
  path: string;
  element: ReactNode;
  children?: RouteConfig[];
  isProtected?: boolean;
  errorElement?: ReactNode;
  hasErrorBoundary?: boolean;
};

const withSuspense = (Component: React.ComponentType) => (
  <Suspense fallback={<ViewportLoading />}>
    <Component />
  </Suspense>
);

const createRoutes = (routes: RouteConfig[]): RouteObject[] =>
  routes.map(({ path, element, children, isProtected, errorElement, hasErrorBoundary }) => ({
    path,
    element: withSuspense(() => (isProtected ? <ProtectedRoute>{element}</ProtectedRoute> : element)),
    children: children && createRoutes(children),
    errorElement:
      !import.meta.env.VITE_DEBUG_MODE && (hasErrorBoundary || errorElement)
        ? withSuspense(() => errorElement ?? <ErrorPage />)
        : undefined,
  }));

const authRoutes: RouteConfig[] = [
  { path: "", element: <Login />, isProtected: false },
  { path: routes.auth.register, element: <Register />, isProtected: false },
  { path: routes.auth.setPassword.linkSent, element: <SetPasswordLinkSentPage />, isProtected: false },
  { path: routes.auth.setPassword.setPassword, element: <SetPasswordPage />, isProtected: false },
  { path: routes.auth.setPassword.completed, element: <SetPasswordCompletedPage />, isProtected: false },
  {
    path: routes.auth.forgetPassword.main,
    element: <ForgetPassword />,
    isProtected: false,
  },
  {
    path: routes.auth.forgetPassword.linkSent,
    element: <ForgetPasswordLinkSent />,
    isProtected: false,
  },
  {
    path: routes.auth.forgetPassword.setPassword,
    element: <ResetPassword />,
    isProtected: false,
  },
  {
    path: routes.auth.forgetPassword.completed,
    element: <ForgetPasswordCompleted />,
    isProtected: false,
  },
];

const mainRoutes: RouteConfig[] = [
  { path: "/", element: <Navigate to={routes.profile.main} replace />, isProtected: true },

  { path: routes.profile.main, element: <ProfilePage />, isProtected: true },
  { path: routes.setting.main, element: <SettingPage />, isProtected: true },

  { path: routes.jobPositions.main, element: <JobPositionListPage />, isProtected: true },
  { path: routes.jobPositions.add, element: <AddJobPositionPage />, isProtected: true },
  { path: routes.jobPositions.edit, element: <AddJobPositionPage />, isProtected: true },
  { path: routes.jobPositions.view, element: <ViewJobPositionPage />, isProtected: true },
  { path: routes.jobPositions.preview, element: <PreviewJobPositionPage />, isProtected: true },
  { path: routes.jobPositions.candidate, element: <CandidatePage />, isProtected: true },

  { path: routes.postHiring.main, element: <PostHiringPage />, isProtected: true },
  { path: routes.postHiring.employeeDetails, element: <PostHiringEmployeeDetailsPage />, isProtected: true },

  { path: routes.financial.main, element: <FinancialPage />, isProtected: true },

  { path: routes.support.main, element: <SupportPage />, isProtected: true },

  { path: routes.interviews.main, element: <InterviewsPage />, isProtected: true },
];

const rootRoutes: RouteConfig[] = [
  {
    path: routes.auth.main,
    element: <AuthLayout />,
    children: authRoutes,
    hasErrorBoundary: true,
  },
  {
    path: routes.auth.verifyAccount,
    element: <VerifyAccountPage />,
    isProtected: false,
    hasErrorBoundary: true,
  },
  {
    path: "/",
    element: <MainLayout />,
    children: mainRoutes,
    hasErrorBoundary: true,
  },
  {
    path: routes.notFound,
    element: <NotFoundPage />,
    hasErrorBoundary: true,
  },
];

const MainRoutes = createBrowserRouter(createRoutes(rootRoutes));

export default MainRoutes;
